import { isJapaneseLocale } from '@toggle/helpers';
import React, { useRef, useState } from 'react';

import { DropdownGroup } from '~/components/dropdown-menu/dropdown-group/DropdownGroup';
import {
  HeaderCategoryItem,
  UnreadConversationType,
} from '~/components/header-navigation';

import { UnreadConversation } from '../unread-conversation/UnreadConversation';
import * as S from './AlfredNavItem.styles';
import * as Shared from './NavItem.styles';

const MAX_DISPLAYED_CONVERSATIONS = 5;
const ONE_LINE_HEIGHT = 48;
const TWO_LINE_HEIGHT = 65;
const THREE_LINE_HEIGHT = 84;

const calculateItemHeight = (question?: string): number => {
  if (!question) {
    return ONE_LINE_HEIGHT;
  }

  const length = question.length;

  if (length <= 45) {
    return ONE_LINE_HEIGHT;
  }

  if (length <= 89) {
    return TWO_LINE_HEIGHT;
  }

  return THREE_LINE_HEIGHT;
};

export type AlfredNavItemProps = {
  item: HeaderCategoryItem;
  isLoading?: boolean;
  isDefault?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  isFinished?: boolean;
  unreadConversations?: UnreadConversationType[];
  onConversationClick?: (conversationId: string) => void;
};

export const AlfredNavItem = ({
  item,
  isLoading,
  isDefault,
  isActive = false,
  disabled,
  isFinished,
  unreadConversations,
  onConversationClick,
}: AlfredNavItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isJapanese = isJapaneseLocale();

  const badgeWidth = isLoading ? 100 : 102;
  const badgeHeight = isLoading ? 34 : 36;
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const itemsToDisplay = Math.min(
    MAX_DISPLAYED_CONVERSATIONS,
    unreadConversations?.length || 0
  );

  const dropdownMaxHeight =
    unreadConversations
      ?.slice(0, itemsToDisplay)
      .reduce((totalHeight, conversation) => {
        const question = conversation.tasks?.at(-1)?.input;
        return totalHeight + calculateItemHeight(question);
      }, 0) || 0;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (unreadConversations?.length) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const conversationDropdownItems =
    unreadConversations?.map(unreadConversation => ({
      key: `conversation-${unreadConversation.id}`,
      label: (
        <UnreadConversation
          status={unreadConversation.status}
          question={unreadConversation.tasks?.at(-1)?.input}
          conversationName={unreadConversation.name}
        />
      ),
      onClick: () => {
        onConversationClick?.(unreadConversation.id);
      },
    })) || [];

  const renderBadge = () => {
    return (
      <S.GradientWrapper
        $isLoading={isLoading}
        $isDefault={isDefault}
        $isFinished={isFinished}
      >
        <Shared.ButtonItem
          data-testid={`header-navigation-item-${item.key}`}
          $isActive={isActive}
          $pillStyle={!!item.pillStyle}
          $isAlfred={item.isAlfred}
          $isJapanese={isJapanese}
          disabled={disabled}
          $width={badgeWidth}
          $height={badgeHeight}
          onClick={e => {
            item.onClick(item, e);
          }}
        >
          <Shared.ItemLeft>
            <Shared.IconWrapper>{item.icon}</Shared.IconWrapper>
            <Shared.MainLabel
              $isAlfred={item.isAlfred}
              $isJapanese={isJapanese}
            >
              {item.label}
            </Shared.MainLabel>
          </Shared.ItemLeft>
        </Shared.ButtonItem>
      </S.GradientWrapper>
    );
  };

  return (
    <Shared.ItemWrapper
      key={item.key}
      $isActive={isActive}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {conversationDropdownItems.length ? (
        <S.StyledDropdown
          key={'alfred'}
          open={isOpen}
          align="end"
          onOpenChange={setIsOpen}
          modal={false}
          triggerNode={renderBadge()}
          $maxHeight={dropdownMaxHeight}
        >
          <DropdownGroup items={conversationDropdownItems} />
        </S.StyledDropdown>
      ) : (
        renderBadge()
      )}
    </Shared.ItemWrapper>
  );
};
